// Closure-encapsulated diff syntax-highlight worker pool.
//
// Replaces four free-floating module-scope mutables that used to live in
// hooks/index.ts (`diffWorkers`, `diffWorkerId`, `diffWorkerRoundRobin`,
// `warmedLangs`) with a single record built by `makeWorkerPool`. index.ts
// keeps exactly one singleton — all the state is hidden inside the closure.
//
// The pool also owns the viewport `IntersectionObserver` that gates which
// pending files actually get dispatched (visible files first, off-screen
// files when they scroll in). The observer is created lazily via
// `ensureObserver(onIntersect)` so DOM access is deferred to first use.

import type { HighlightResponse } from "../diff-highlight-worker";

export interface DiffWorkerPool {
  // Send code for highlighting; resolves with the worker's response buffer
  // (null when the worker reports failure). Round-robin scheduling across
  // workers.
  readonly dispatch: (
    code: string,
    lang: string,
  ) => Promise<ArrayBuffer | null>;

  // Hint to workers that a given language will be needed soon, so they can
  // start compiling its grammar in the background. Idempotent per language.
  readonly warmup: (lang: string) => void;

  // Lazy IntersectionObserver; the caller passes its own intersection handler
  // (which is responsible for unobserving and dispatching). The pool owns the
  // observer's lifecycle so it can be torn down with `dispose`.
  readonly ensureObserver: (
    onIntersect: (entry: IntersectionObserverEntry) => void,
  ) => IntersectionObserver;

  // Free all workers and disconnect the observer. After dispose the pool is
  // unusable; create a new one if needed.
  readonly dispose: () => void;
}

interface PendingEntry {
  readonly resolve: (buf: ArrayBuffer | null) => void;
  readonly workerIndex: number;
}

// A slot that crashes this many times in a row is marked dead and never
// respawned: a worker that 404s or fails module load used to re-enter
// construct → error → terminate forever, burning CPU on every failure.
// A run of successful responses resets the slot's budget, so a worker
// that crashes occasionally (e.g. one pathological file) recovers.
export const MAX_RESPAWNS = 3;
export const SUCCESS_RESET_WINDOW = 20;

interface PoolState {
  // A dead (exhausted-budget) slot is null — dispatch skips it, and the
  // slot index space stays stable for pending-entry bookkeeping.
  readonly workers: (Worker | null)[];
  nextId: number;
  roundRobin: number;
  readonly pending: Map<number, PendingEntry>;
  readonly warmed: Set<string>;
  observer: IntersectionObserver | null;
  readonly respawnAttempts: number[];
  readonly successes: number[];
}

const makeWorker = (state: PoolState, index: number): Worker => {
  const w = new Worker("/assets/diff-highlight-worker.js", { type: "module" });
  w.onmessage = (e: MessageEvent<HighlightResponse>) => {
    const entry = state.pending.get(e.data.id);
    if (entry) {
      state.pending.delete(e.data.id);
      const n = (state.successes[index] ?? 0) + 1;
      if (n >= SUCCESS_RESET_WINDOW) {
        state.successes[index] = 0;
        state.respawnAttempts[index] = 0;
      } else {
        state.successes[index] = n;
      }
      entry.resolve(e.data.buf);
    }
  };
  w.onerror = (e: ErrorEvent) => {
    console.error("[diff-worker]", e.message);
    handleWorkerError(state, index);
  };
  return w;
};

const handleWorkerError = (state: PoolState, index: number): void => {
  const dead = state.workers[index];
  if (dead) dead.terminate();
  // Resolve the crashed worker's pending requests with null so callers
  // see a clean failure instead of a hung promise.
  for (const [id, entry] of [...state.pending]) {
    if (entry.workerIndex !== index) continue;
    state.pending.delete(id);
    entry.resolve(null);
  }
  if (state.workers.length === 0) return; // dispose raced with onerror
  if ((state.respawnAttempts[index] ?? 0) >= MAX_RESPAWNS) {
    // Terminal: the slot stays null. Dispatch routes around it; when
    // every slot is dead, dispatch resolves null directly.
    state.workers[index] = null;
    console.error(
      `[diff-worker] slot ${index} exceeded respawn budget (${MAX_RESPAWNS}); marking dead`,
    );
    return;
  }
  state.respawnAttempts[index] = (state.respawnAttempts[index] ?? 0) + 1;
  state.successes[index] = 0;
  replaceWorker(state, index);
};

// Replace a crashed worker in-place and resolve all of its pending requests
// with null so callers see a clean failure instead of a hung promise. The
// slot is reused so round-robin scheduling stays stable. After replacement,
// the fresh worker is rewarmed for every language the pool has already
// warmed so future dispatches don't pay the grammar-compile cost.
const replaceWorker = (state: PoolState, index: number): void => {
  const dead = state.workers[index];
  if (dead) dead.terminate();
  if (state.workers.length === 0) return; // dispose raced with onerror
  const fresh = makeWorker(state, index);
  state.workers[index] = fresh;
  for (const lang of state.warmed)
    fresh.postMessage({ type: "warmup", id: -1, code: "", lang });
};

export const makeWorkerPool = (count: number): DiffWorkerPool => {
  const state: PoolState = {
    workers: [],
    nextId: 0,
    roundRobin: 0,
    pending: new Map(),
    warmed: new Set(),
    observer: null,
    respawnAttempts: [],
    successes: [],
  };

  // Eagerly create workers — they start compiling tree-sitter.wasm immediately,
  // so by the time a diff tab opens the WASM runtime is already warm.
  for (let i = 0; i < count; i++) {
    state.workers.push(makeWorker(state, i));
    state.respawnAttempts.push(0);
    state.successes.push(0);
  }

  const dispatch = (
    code: string,
    lang: string,
  ): Promise<ArrayBuffer | null> => {
    if (state.workers.length === 0) return Promise.resolve(null);
    // Round-robin across LIVE slots only; a dead (exhausted-budget) slot
    // is null and is skipped. All slots dead → clean failure.
    const live: number[] = [];
    for (let i = 0; i < state.workers.length; i++) {
      if (state.workers[i] !== null) live.push(i);
    }
    if (live.length === 0) return Promise.resolve(null);
    const id = state.nextId++;
    const workerIndex = live[state.roundRobin % live.length]!;
    const worker = state.workers[workerIndex]!;
    state.roundRobin++;
    return new Promise((resolve) => {
      state.pending.set(id, { resolve, workerIndex });
      worker.postMessage({ id, code, lang });
    });
  };

  const warmup = (lang: string): void => {
    if (!lang || state.warmed.has(lang)) return;
    state.warmed.add(lang);
    for (const w of state.workers)
      w?.postMessage({ type: "warmup", id: -1, code: "", lang });
  };

  const ensureObserver = (
    onIntersect: (entry: IntersectionObserverEntry) => void,
  ): IntersectionObserver => {
    if (state.observer) return state.observer;
    state.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          onIntersect(entry);
        }
      },
      { rootMargin: "300px" }, // 300px lookahead so files just below the fold start early
    );
    return state.observer;
  };

  const dispose = (): void => {
    for (const w of state.workers) w?.terminate();
    state.workers.length = 0;
    state.pending.clear();
    state.warmed.clear();
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
  };

  return { dispatch, warmup, ensureObserver, dispose };
};
